import { useMemo } from 'react';

import {
  U21Modal,
  U21Form,
  U21FormField,
  U21FormFieldTypes,
  U21Spacer,
} from 'app/shared/u21-ui/components';

import { RiskModelSummary } from 'app/modules/riskRatings/responses';
import { useApplyRiskScoreOverride } from 'app/modules/riskScore/queries/useApplyRiskScoreOverride';
import { RiskScorePicker } from 'app/modules/riskScore/components/RiskScorePicker';
import { EntityRiskScoreDisplay } from 'app/modules/riskScore/components/EntityRiskScore';
import { startOfDay } from 'date-fns';
import { EntityLatestRiskScore } from 'app/modules/riskScore/models';

interface Props {
  open: boolean;
  onClose: () => void;
  latestRiskScore: EntityLatestRiskScore;
  riskModelSummary: Pick<
    RiskModelSummary,
    'high_risk_floor' | 'id' | 'low_risk_ceiling' | 'title'
  >;
}

interface OverrideFormValues {
  score: number;
  comment: string;
  expires_at?: Date;
}

const FORM_ID = 'risk-score-override-form';

export const RiskScoreOverrideModal = ({
  open,
  onClose,
  latestRiskScore,
  riskModelSummary,
}: Props) => {
  const { score } = latestRiskScore;
  const { high_risk_floor: highRiskFloor, low_risk_ceiling: lowRiskCeiling } =
    riskModelSummary;

  const { mutateAsync, isPending } = useApplyRiskScoreOverride(
    latestRiskScore.entity_external_id,
  );

  const initialValues: Partial<OverrideFormValues> = useMemo(
    () => ({ score: Math.round(score) }),
    [score],
  );

  return (
    <U21Modal
      open={open}
      onClose={onClose}
      subtitle={
        <U21Spacer horizontal useFlexGap>
          Current risk score:
          <EntityRiskScoreDisplay
            disablePopover
            latestRiskScore={latestRiskScore}
            riskModelSummary={riskModelSummary}
          />
        </U21Spacer>
      }
      title="Override Risk Score"
      actionButtonProps={{
        children: 'Apply Override',
        form: FORM_ID,
        type: 'submit',
        loading: isPending,
      }}
    >
      <U21Form<OverrideFormValues>
        id={FORM_ID}
        onSubmit={async (values: OverrideFormValues) => {
          try {
            await mutateAsync({
              score: values.score,
              comment: values.comment.trim(),
              expires_at: values.expires_at?.toISOString(),
            });
            onClose();
          } catch {}
        }}
        initialValues={initialValues}
      >
        <U21Spacer spacing={2}>
          <RiskScorePicker
            highRiskFloor={highRiskFloor}
            lowRiskCeiling={lowRiskCeiling}
          />
          <U21FormField
            label="Reason"
            name={'comment' satisfies keyof OverrideFormValues}
            type={U21FormFieldTypes.TEXTAREA}
            required
            fieldProps={{
              placeholder:
                'Describe the reason for this override (required for audit trail).',
            }}
            validate={(value?: string) =>
              !value?.trim() ? 'Reason is required.' : undefined
            }
          />
          <U21FormField<Date | undefined>
            label="Expiration"
            name={'expires_at' satisfies keyof OverrideFormValues}
            type={U21FormFieldTypes.DATE_TIME_PICKER}
            description="Override will automatically expire on this date."
            fieldProps={{
              minDate: startOfDay(new Date()),
              placeholder: 'No expiration',
            }}
            validate={(value) => {
              if (!value) {
                return undefined;
              }
              if (value < new Date()) {
                return 'Please select an expiration in the future.';
              }
              return undefined;
            }}
          />
        </U21Spacer>
      </U21Form>
    </U21Modal>
  );
};
